<template>
  <div>
    <v-dialog
      v-model="opened"
      content-class="worker-dialog"
      max-width="750"
      persistent
    >
      <h3 class="worker-dialog__title">{{ $t(title) }}</h3>

      <v-btn class="close-btn" icon @click="opened = false">
        <v-icon size="27" color="#464646"> mdi-close-circle-outline </v-icon>
      </v-btn>

      <v-form
        ref="form"
        class="worker-dialog__form"
        :disabled="sending"
        @submit.prevent="data ? onUpdate() : onCreate()"
      >
        <div class="worker-dialog__form-fields">
          <div class="worker-dialog__form-fields-section">
            <fieldset class="mr-3">
              <legend>{{ $t('notification.auto.label') }}</legend>

              <div class="d-flex">
                <v-autocomplete
                  ref="number"
                  v-model="selectedAuto"
                  class="mr-2"
                  item-text="number"
                  outlined
                  dense
                  hide-no-data
                  chips
                  deletable-chips
                  small-chips
                  :search-input.sync="formData.number"
                  :items="foundCars"
                  :label="$t('auto.fields.number')"
                  :loading="numberLoading"
                  :success-messages="noCarsFound ? $t('sidebar.addAuto') : ''"
                  :menu-props="{ maxHeight: 200 }"
                  :rules="[isRequired]"
                  @update:search-input="onNumberInput"
                >
                  <template #append>
                    <div class="worker-dialog__number-search-icon">
                      <v-btn
                        v-if="noCarsFound"
                        color="success"
                        icon
                        x-small
                        rounded
                        :to="{
                          name: 'add-auto',
                          query: { number: formData.number },
                        }"
                      >
                        <v-icon>
                          mdi-plus
                        </v-icon>
                      </v-btn>

                      <v-icon v-else small>
                        mdi-magnify
                      </v-icon>
                    </div>
                  </template>
                </v-autocomplete>

                <v-text-field
                  v-model="formData.investor"
                  class="ml-2"
                  outlined
                  dense
                  disabled
                  :label="$t('auto.fields.investor')"
                />
              </div>

              <div class="d-flex">
                <v-text-field
                  v-model="formData.brand"
                  class="mr-2"
                  outlined
                  dense
                  disabled
                  :label="$t('auto.fields.brand')"
                />

                <v-text-field
                  v-model="formData.model"
                  class="ml-2"
                  outlined
                  dense
                  disabled
                  :label="$t('auto.fields.model')"
                />
              </div>

              <div class="d-flex">
                <v-select
                  v-model="formData.shift"
                  class="mr-2"
                  item-value="id"
                  item-text="name"
                  outlined
                  dense
                  disabled
                  :items="rentTypes"
                  :label="$t('workersList.shift')"
                />

                <v-text-field
                  v-model="formData.cost"
                  class="ml-2"
                  outlined
                  dense
                  disabled
                  :label="$t('common.table.cost')"
                />
              </div>

              <v-select
                v-model="formData.status"
                outlined
                dense
                item-text="name"
                item-value="id"
                :label="$t('common.fields.status')"
                :items="filteredStatuses"
                :item-disabled="isDisabledStatus"
                :rules="[isRequired]"
                :disabled="!$can('workdesk_edit_driver_and_auto_status')"
                :loading="statusUpdating"
              />
            </fieldset>

            <fieldset>
              <legend>{{ $t('notification.driver.label') }}</legend>

              <v-autocomplete
                ref="driver"
                v-model="selectedDriver"
                item-text="phone"
                dense
                outlined
                chips
                deletable-chips
                small-chips
                hide-no-data
                :items="foundDrivers"
                :search-input.sync="formData.phone"
                :label="$t('notification.phone.label')"
                :loading="driverLoading"
                :menu-props="{ maxHeight: 200 }"
                :success-messages="
                  noDriversFound ? $t('common.btns.addDriver') : ''
                "
                :disabled="!$can('workdesk_edit_driver_and_auto_status')"
                @update:search-input="onDriverInput"
              >
                <template #append>
                  <div class="worker-dialog__number-search-icon">
                    <v-btn
                      v-if="noDriversFound"
                      color="success"
                      icon
                      x-small
                      rounded
                      :to="{
                        name: 'add-driver',
                        query: { number: formData.phone },
                      }"
                    >
                      <v-icon>
                        mdi-plus
                      </v-icon>
                    </v-btn>

                    <v-icon v-else small>
                      mdi-magnify
                    </v-icon>
                  </div>
                </template>
              </v-autocomplete>

              <v-text-field
                v-model="formData.name"
                dense
                outlined
                disabled
                :label="$t('investorsList.tableHeaders.name')"
              />

              <v-text-field
                v-model="formData.phoneRelative"
                dense
                outlined
                disabled
                :label="$t('common.fields.phoneRelative')"
              />

              <div class="d-flex">
                <v-text-field
                  v-model="formData.duty"
                  class="mr-2"
                  dense
                  outlined
                  :disabled="!selectedDriver || !$can('workdesk_edit_cash')"
                  :label="$t('common.table.duty')"
                />
                <v-text-field
                  v-model="formData.dutyComment"
                  class="ml-2"
                  dense
                  outlined
                  :disabled="
                    !selectedDriver ||
                      !$can('workdesk_edit_cash') ||
                      !formData.duty
                  "
                  :label="$t('common.fields.comment')"
                />
              </div>
            </fieldset>
          </div>

          <div
            v-if="$can('workdesk_edit_explanation')"
            class="worker-dialog__form-fields-section"
          >
            <fieldset>
              <legend>{{ $t('workersList.explanation') }}</legend>
              <v-text-field
                v-model="formData.explanation"
                dense
                outlined
                hide-details
                full-width
              />
            </fieldset>
          </div>

          <div
            v-if="$can('workdesk_edit_cash')"
            class="worker-dialog__form-fields-section"
          >
            <fieldset>
              <legend>{{ $t('common.fields.cashbox') }}</legend>

              <v-progress-linear
                v-if="cashboxLoading"
                color="warning"
                class="my-3"
                height="6"
                indeterminate
                rounded
              />

              <div v-else class="worker-dialog__form-cashbox">
                <div class="worker-dialog__form-cashbox-type">
                  <h4 class="mb-3">{{ $t('common.table.coming') }}</h4>

                  <v-expansion-panels
                    v-model="incomePanels"
                    multiple
                    :disabled="cashboxDisabled"
                  >
                    <v-expansion-panel
                      v-for="{ value, key } in incomeTypes"
                      :key="key"
                    >
                      <v-expansion-panel-header
                        :disable-icon-rotate="!!formData.types[`${key}-amount`]"
                      >
                        <template
                          v-if="hasErrors(`type-${key}`)"
                          v-slot:actions
                        >
                          <v-icon class="mr-1" color="error" small>
                            mdi-alert-circle
                          </v-icon>
                        </template>
                        <template
                          v-else-if="formData.types[`${key}-amount`]"
                          v-slot:actions
                        >
                          <v-icon class="mr-1" color="success" small>
                            mdi-checkbox-marked-circle
                          </v-icon>
                        </template>

                        {{ value }}
                      </v-expansion-panel-header>

                      <v-expansion-panel-content>
                        <div class="d-flex">
                          <v-text-field
                            v-model="formData.types[`${key}-amount`]"
                            :ref="`type-${key}`"
                            class="mr-1 mb-3"
                            outlined
                            dense
                            hide-details
                            :label="$t('common.fields.sum')"
                            :rules="[onlyNumbers]"
                          />

                          <v-select
                            v-model="formData.types[`${key}-payment_type`]"
                            :ref="`type-${key}`"
                            class="ml-1"
                            outlined
                            dense
                            item-text="name"
                            item-value="id"
                            hide-details
                            :items="paymentTypes"
                            :label="$t('common.fields.paymentMethod')"
                            :rules="[
                              (v) => {
                                if (!formData.types[`${key}-amount`]) {
                                  return true;
                                }

                                return isRequired(v);
                              },
                            ]"
                          />
                        </div>

                        <v-text-field
                          v-model="formData.types[`${key}-comment`]"
                          outlined
                          dense
                          hide-details
                          :label="$t('common.fields.comment')"
                        />
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </div>

                <v-divider vertical />

                <div class="worker-dialog__form-cashbox-type">
                  <h4>{{ $t('common.table.consumption') }}</h4>

                  <v-expansion-panels
                    v-model="expensesPanels"
                    multiple
                    :disabled="!selectedAuto"
                  >
                    <v-expansion-panel
                      v-for="{ value, key } in expensesTypes"
                      :key="key"
                    >
                      <v-expansion-panel-header
                        :disable-icon-rotate="!!formData.types[`${key}-amount`]"
                      >
                        <template
                          v-if="hasErrors(`type-${key}`)"
                          v-slot:actions
                        >
                          <v-icon class="mr-1" color="error" small>
                            mdi-alert-circle
                          </v-icon>
                        </template>
                        <template
                          v-else-if="formData.types[`${key}-amount`]"
                          v-slot:actions
                        >
                          <v-icon class="mr-1" color="success" small>
                            mdi-checkbox-marked-circle
                          </v-icon>
                        </template>

                        {{ value }}
                      </v-expansion-panel-header>
                      <v-expansion-panel-content>
                        <div class="d-flex">
                          <v-text-field
                            v-model="formData.types[`${key}-amount`]"
                            :ref="`type-${key}`"
                            class="mr-1"
                            outlined
                            dense
                            hide-details
                            :label="$t('common.fields.sum')"
                            :rules="[onlyNumbers]"
                          />
                          <v-text-field
                            v-model="formData.types[`${key}-comment`]"
                            class="ml-1"
                            outlined
                            dense
                            hide-details
                            :label="$t('common.fields.comment')"
                          />
                        </div>
                      </v-expansion-panel-content>
                    </v-expansion-panel>
                  </v-expansion-panels>
                </div>
              </div>
            </fieldset>
          </div>
        </div>

        <v-btn
          class="worker-dialog__form-submit"
          type="submit"
          color="warning"
          elevation="0"
          :loading="sending"
        >
          {{ $t('common.btns.save') }}
        </v-btn>
      </v-form>
    </v-dialog>

    <v-snackbar
      v-model="snack.value"
      absolute
      bottom
      right
      :color="snack.color"
      :timeout="3000"
    >
      {{ snack.message }}
    </v-snackbar>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    data: {
      type: [Object, null],
      default: null,
    },
  },
  model: {
    prop: 'value',
    event: 'change',
  },
  data: () => ({
    sending: false,
    cashboxLoading: false,
    numberLoading: false,
    driverLoading: false,
    statusUpdating: false,
    isVocation: true,
    selectedAuto: null,
    selectedDriver: null,
    incomePanels: [],
    expensesPanels: [],

    snack: {
      value: false,
      message: '',
      color: '',
    },

    formData: {
      number: '',
      investor: '',
      brand: '',
      model: '',
      shift: '',
      cost: '',
      status: '',
      phone: '',
      name: '',
      phoneRelative: '',
      duty: 0,
      dutyComment: '',
      types: {},
      explanation: '',
    },
  }),
  watch: {
    opened(val) {
      if (val) {
        this.fillForm();
      } else {
        this.$refs.form.reset();
        setTimeout(() => this.$emit('closed'), 300);
      }
    },
    'formData.number': {
      handler(val) {
        if (!val) return;
        if (this.data && this.data.car.number === val) return;

        this.searchCars(val);
      },
    },
    'formData.phone': {
      async handler(val) {
        if (!val) return;
        if (this.data && this.data.driver?.phone === val) return;

        this.searchDrivers(val);
      },
    },
    'formData.status': {
      async handler(val) {
        if (val === '8' || val === '9') {
          delete this.formData.types['2-amount'];
          delete this.formData.types['2-comment'];
          delete this.formData.types['2-payment_type'];
          this.incomePanels = this.incomePanels.filter((i) => i !== 1);
        }
      },
    },
    selectedAuto(number) {
      if (!number) {
        this.formData.types = {};
        this.incomePanels = [];
        this.expensesPanels = [];

        return;
      }

      const auto = this.foundCars.find((item) => item.number === number);

      this.formData.investor = auto.investor;
      this.formData.brand = auto.brand;
      this.formData.model = auto.model;
      this.formData.cost = auto.rentShiftPrice;

      this.formData.shift = '3';
    },
    selectedDriver(phone) {
      if (!phone) {
        this.formData.name = '';
        this.formData.phoneRelative = '';
        this.formData.status = '';

        return;
      }

      const driver = this.foundDrivers.find((item) => item.phone === phone);

      this.formData.name = driver.name;
      this.formData.phoneRelative = driver.phoneRelative;
      this.formData.status = '1';
    },
    vocationCheckRequestParams(data) {
      if (!data.car_id || !data.driver_id) {
        return;
      }

      this.setVocationAvalible();
    },
  },
  computed: {
    ...mapState({
      foundCars: (state) => state['workers-board'].foundCars,
      foundDrivers: (state) => state['workers-board'].foundDrivers,
      expensesTypes: (state) => state['workers-board'].expensesTypes,
      incomeTypes: (state) => state['workers-board'].incomeTypes,
      paymentTypes: (state) => state['workers-board'].paymentTypes,
      rentTypes: (state) => state['workers-board'].rentTypes,
      carStatuses: (state) => state['workers-board'].carStatuses,
    }),
    opened: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('change', val);
      },
    },
    title() {
      return this.data ? 'common.btns.edit' : 'workersAdd.title';
    },
    noCarsFound() {
      return (
        this.formData.number && !this.foundCars.length && !this.numberLoading
      );
    },
    noDriversFound() {
      return (
        this.formData.phone && !this.foundDrivers.length && !this.driverLoading
      );
    },
    cashboxDisabled() {
      return !this.selectedAuto || !this.selectedDriver;
    },
    filteredStatuses() {
      return this.carStatuses.filter(({ id }) => Number(id) !== 0);
    },
    requestData() {
      const cardId = this.foundCars.find(
        ({ number }) => number === this.selectedAuto,
      )?.id;
      const driverId = this.foundDrivers.find(
        ({ phone }) => phone === this.selectedDriver,
      )?.id;

      const transactions = Object.entries(this.formData.types).reduce(
        (res, [key, value]) => {
          if (!value) return res;

          const [id, param] = key.split('-');
          const transaction = res.find((item) => item.type === id);

          if (transaction) {
            transaction[param] = value;
          } else {
            res.push({
              type: id,
              [param]: value,
            });
          }

          return res;
        },
        [],
      );

      const data = {
        car_id: cardId,
        car_status: this.formData.status,
        driver_id: driverId || null,
        driver_debt: this.formData.duty || 0,
        driver_debt_comment: this.formData.dutyComment,
        transactions,
      };

      if (this.formData.explanation) {
        data.explanation = this.formData.explanation;
      }

      return data;
    },
    vocationCheckRequestParams() {
      return {
        car_id: this.requestData.car_id,
        driver_id: this.requestData.driver_id,
      };
    },
  },
  methods: {
    isRequired(v) {
      return !!v || this.$t('common.fields.validation.field.required');
    },
    onlyNumbers(v) {
      if (!v) return true;
      return (
        /^[0-9]{1,12}$/.test(v) || this.$t('common.fields.validation.numbers')
      );
    },
    onNumberInput(v) {
      if (!v) return null;
      this.selectedAuto = null;
      return this.$refs.number && this.$refs.number.resetValidation();
    },
    onDriverInput(v) {
      if (!v) return null;
      this.selectedDriver = null;
      return this.$refs.driver && this.$refs.driver.resetValidation();
    },
    async searchCars(value) {
      this.numberLoading = true;
      await this.$store.dispatch('workers-board/searchExistingCars', value);
      this.numberLoading = false;
    },
    async searchDrivers(value) {
      this.driverLoading = true;
      await this.$store.dispatch('workers-board/searchExistingDrivers', value);
      this.driverLoading = false;
    },
    async fillForm() {
      if (!this.data) return;
      const { car, driver, transactions } = this.data;

      this.cashboxLoading = true;

      await this.searchCars(car.number);

      if (driver) {
        await this.searchDrivers(driver.user.phone);
        this.selectedDriver = driver.user.phone;
        this.formData.duty = Number(this.data.driver_debt);
        this.formData.dutyComment = this.data.driver_debt_comment;
      }

      this.selectedAuto = car.number;

      transactions.forEach((item) => {
        this.formData.types[`${item.type}-amount`] = Number(item.amount);
        this.formData.types[`${item.type}-comment`] = item.comment;
        this.formData.types[`${item.type}-payment_type`] = String(
          item.payment_type,
        );

        this.incomeTypes.forEach((obj, idx) => {
          if (Number(obj.key) === item.type) this.incomePanels.push(idx);
        });

        this.expensesTypes.forEach((obj, idx) => {
          if (Number(obj.key) === item.type) this.expensesPanels.push(idx);
        });
      });

      this.formData.status = String(this.data.car_status);
      this.formData.explanation = this.data.explanation;

      this.cashboxLoading = false;
    },
    proccessResponse({ message, error }) {
      const snack = {
        value: true,
        color: 'success',
        message,
      };

      if (error) {
        snack.message = error;
        snack.color = 'error';
      }

      if (message) {
        this.opened = false;
        this.$emit('success');
      }

      this.snack = snack;
      this.sending = false;
    },
    async onCreate() {
      const isValid = this.$refs.form.validate();

      if (!isValid) return null;

      this.sending = true;

      const response = await this.$store.dispatch(
        'workers-board/addNew',
        this.requestData,
      );

      this.proccessResponse(response);

      return null;
    },
    async onUpdate() {
      const isValid = this.$refs.form.validate();

      if (!isValid) return null;

      this.sending = true;

      const response = await this.$store.dispatch('workers-board/updateItem', {
        id: this.data.id,
        formData: this.requestData,
      });

      this.proccessResponse(response);

      return null;
    },
    hasErrors(ref) {
      const arr = this.$refs[ref];

      if (arr) {
        return arr.some(({ validationState }) => validationState === 'error');
      }

      return false;
    },
    isDisabledStatus({ id }) {
      // const isBuyout = Number(id) === 13;

      // if (!this.$can('workdesk_edit_on_buyout') && isBuyout) {
      //   return true;
      // }

      return this.checkIsDriverRequired(id) || this.checkIsVocationDisabled(id);
    },
    checkIsDriverRequired(id) {
      let isDriverRequire = false;

      switch (Number(id)) {
        case 1:
        case 5:
        case 8:
        case 9:
        case 11:
        case 13:
          isDriverRequire = true;
          break;
        default:
          isDriverRequire = false;
      }

      return this.selectedDriver ? !isDriverRequire : isDriverRequire;
    },
    checkIsVocationDisabled(id) {
      const statusId = Number(id);
      const isVocationDay = statusId === 8 || statusId === 9;
      const isVocationAvalible = !this.isVocation;

      if (!isVocationDay) return false;
      return isVocationAvalible;
    },
    async setVocationAvalible() {
      this.statusUpdating = true;
      const { data } = await this.$store.dispatch(
        'workers-board/checkIsVocationAvalible',
        this.vocationCheckRequestParams,
      );

      this.isVocation = data;
      this.statusUpdating = false;
    },
  },
};
</script>

<style lang="scss">
.worker-dialog {
  padding: 15px;
  background-color: #fff;

  &__title {
    text-align: center;
    font-size: 18px;
  }

  &__form {
    &-fields {
      margin-top: 10px;
    }

    &-fields {
      max-height: 500px;
      overflow-y: auto;

      @media (max-width: 768px) {
        max-height: 400px;
      }
    }

    &-fields-section {
      display: flex;

      @media (max-width: 768px) {
        flex-direction: column;
      }

      > fieldset {
        width: 100%;
        padding: 10px;
        margin-bottom: 15px;
        border: 1px solid #777;
        border-radius: 5px;
        background-color: #eee;

        > legend {
          font-size: 16px;
          font-weight: 600;
          line-height: 1;
          border-radius: 5px;
          padding: 2px 4px;
          color: #777;
          background-color: #fff;
        }
      }
    }

    &-submit {
      display: block;
      max-width: 400px;
      width: 100%;
      margin: 20px auto 0 !important;
    }

    &-cashbox {
      display: flex;
      justify-content: space-between;

      @media (max-width: 768px) {
        flex-direction: column;
        align-items: center;
        justify-content: center;
      }
    }

    &-cashbox-type {
      width: 100%;
      padding: 0 10px;
      border-radius: 5px;

      > h4 {
        margin-bottom: 8px;
        font-size: 14px;
        text-align: center;
        color: #777;
      }

      &:first-child {
        margin-right: 10px;

        @media (max-width: 768px) {
          margin-right: 0;
          margin-bottom: 25px;
        }
      }

      &:last-child {
        margin-left: 10px;

        @media (max-width: 768px) {
          margin-left: 0;
        }
      }
    }

    .d-flex .v-text-field,
    .d-flex .v-select {
      width: 50%;
    }

    .v-text-field,
    .v-select {
      width: 100%;

      .v-text-field__details {
        padding-left: 3px;
      }

      .v-input__slot {
        background-color: #fff;

        input {
          font-size: 14px !important;
        }
      }
    }
  }

  .v-input--is-disabled fieldset {
    color: rgba(0, 0, 0, 0.38) !important;

    legend {
      color: rgba(0, 0, 0, 0.87) !important;
    }
  }

  .v-input--is-disabled .v-text-field__slot {
    label {
      color: rgba(0, 0, 0, 0.45);
    }

    input {
      color: rgba(0, 0, 0, 0.45);
    }
  }

  &__number-search-icon {
    margin-top: 4px;
  }

  .v-expansion-panel-header {
    padding: 10px 15px;
    min-height: 35px;
  }

  .v-expansion-panel-content__wrap {
    padding: 10px 15px;

    .v-label,
    .v-select__selection,
    input {
      font-size: 14px;
    }
  }
}
</style>
